import React, { useCallback, useRef, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

import { EstimateActions, EstimateType } from '@zego/enums';
import useAttribution from '@zego/hooks/useAttribution';
import useLogout from '@zego/hooks/useLogout';
import useStableCallback from '@zego/hooks/useStableCallback';
import sentry from '@zego/sentry';
import { BREAKPOINTS } from '@zego/theme';
import { useTracking } from '@zego/tracking';
import { withTrackingScope } from '@zego/tracking';

import H3 from '@zego/components/atoms/H3';

import QuickEstimatesAuthModal from '../../forms/QuickEstimatesAuthModal';
import createUseEstimateForm from '../../hooks/createUseEstimateForm';
import usePrivateHireEstimateResult from '../usePrivateHireEstimateResult';
import PrivateHireQuickEstimatesFormCore from './PrivateHireQuickEstimatesFormCore';

/**
 * @param {Object} props
 * @param {string} [props.source]
 */
function PrivateHireQuickEstimatesForm({ viewer, source, ...props }) {
  const logout = useLogout();
  const [error, setError] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const attribution = useAttribution();
  const { trackEvent } = useTracking();

  const [submittingKind, setSubmittingKind] = useState(null);

  const deferredSubmit = useRef(null);

  const openModal = useCallback(() => setModalIsOpen(true), []);
  const closeModal = useCallback(() => setModalIsOpen(false), []);

  const name = viewer?.currentUser?.fullName;
  const isLoggedIn = !!name;

  const usePrivateHireEstimateForm = createUseEstimateForm(
    '/private-hire/estimates',
    usePrivateHireEstimateResult,
  );

  const _onSubmit = useStableCallback(
    usePrivateHireEstimateForm(null, values => ({
      action: EstimateActions.UPDATE_DATA,
      data: {
        person: {
          dateOfBirth: values.bday,
          postcode: values.postcode.trim(),
        },
        vehicle: {
          noClaimsDuration: values.noClaimsDuration,
        },
      },
      attribution,
    })),
    [],
  );

  const wrappedSubmit = useCallback(
    (values, onComplete) => {
      trackEvent('Website: Quick Estimates - Short form submitted');

      _onSubmit(
        { ...values, __estimate_form_source: source },
        {
          onError: err => {
            if (err) {
              sentry.captureException(err);
            }

            setModalIsOpen(false);
            setError('Something went wrong');
            onComplete();
            setSubmittingKind(null);
          },
        },
      );
    },
    [_onSubmit, source, trackEvent],
  );

  const _anonymousOnSubmit = useCallback(
    (values, cb) => {
      setSubmittingKind('anonymous');
      wrappedSubmit(values, cb);
    },
    [wrappedSubmit],
  );

  const _authenticatedOnSubmit = useCallback(
    (values, cb) => {
      deferredSubmit.current = () => {
        wrappedSubmit(values, cb);
      };
      openModal();
    },
    [openModal, wrappedSubmit],
  );

  return (
    <>
      <H3
        css={{
          fontSize: 22,
          lineHeight: '28px',
          [BREAKPOINTS.small]: { fontSize: 22, lineHeight: '28px' },
        }}
      >
        Get a quote in minutes
      </H3>
      <PrivateHireQuickEstimatesFormCore
        {...props}
        source={source}
        error={error}
        submitting={submittingKind === 'anonymous'}
        onSubmit={isLoggedIn ? _authenticatedOnSubmit : _anonymousOnSubmit}
      />
      <QuickEstimatesAuthModal
        name={name}
        isOpen={modalIsOpen}
        onClose={closeModal}
        onAccept={() => {
          setSubmittingKind('continue');
          deferredSubmit.current();
        }}
        onReject={async () => {
          setSubmittingKind('logout');
          await logout();
          deferredSubmit.current();
        }}
        isAccepting={submittingKind === 'continue'}
        isRejecting={submittingKind === 'logout'}
      />
    </>
  );
}

const PrivateHireQuickEstimatesFormContainer = createFragmentContainer(
  PrivateHireQuickEstimatesForm,
  {
    viewer: graphql`
      fragment PrivateHireQuickEstimatesForm_viewer on Viewer {
        currentUser {
          fullName
        }
      }
    `,
  },
);

export default withTrackingScope({
  estimateType: EstimateType.GB_PRIVATE_HIRE,
  featureName: 'Private Hire Quick Estimates Widget',
})(PrivateHireQuickEstimatesFormContainer);
